/**
 * Bathe Engine - Production-Grade Wealth Experience
 * 
 * LOCKED CUT - Final Specification
 * Total Duration: 2:10 (130 seconds)
 * 
 * This is DYNAMIC inside the app, not a pre-rendered video:
 * - Clips sequenced in-app
 * - Audio layered (music + voice + subliminal)
 * - Text overlays timed
 * - Subliminal flashes triggered at specific timestamps
 * 
 * CRITICAL: Slow, immersive, minimal. NOT a video player.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Volume2, VolumeX } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// WEALTH BATHE - LOCKED CUT SPECIFICATION
// =============================================================================

const WEALTH_BATHE = {
  title: 'Wealth',
  color: '#CBA153',
  totalDuration: 130000, // 2:10 in milliseconds
  
  scenes: [
    {
      id: 'arrival',
      name: 'ARRIVAL',
      startTime: 0,
      duration: 10000, // 0:00-0:10
      description: 'Sunrise / horizon / light emerging',
      voice: "Just for a moment… allow yourself to arrive…",
      text: null,
    },
    {
      id: 'opening',
      name: 'OPENING', 
      startTime: 10000,
      duration: 15000, // 0:10-0:25
      description: 'Ocean / skyline / mountains',
      voice: "Let everything slow down…",
      text: null,
    },
    {
      id: 'state-entry',
      name: 'STATE ENTRY',
      startTime: 25000,
      duration: 20000, // 0:25-0:45
      description: 'Interior with light movement - Curtains / sunlight / still space',
      voice: "Take a slow breath in… and let your body open…",
      text: "It is safe to receive",
    },
    {
      id: 'entering-life',
      name: 'ENTERING LIFE',
      startTime: 45000,
      duration: 20000, // 0:45-1:05
      description: 'Walking into home / touching environment',
      voice: "You are no longer trying to create this… You are stepping into it…",
      text: null,
    },
    {
      id: 'normalised-wealth',
      name: 'NORMALISED WEALTH',
      startTime: 65000,
      duration: 20000, // 1:05-1:25
      description: 'Coffee / sitting / view',
      voice: "Money flows to you… naturally… consistently…",
      text: "Receiving is your nature",
    },
    {
      id: 'expansion',
      name: 'EXPANSION',
      startTime: 85000,
      duration: 20000, // 1:25-1:45
      description: 'Driving / travel / motion',
      voice: "There is nothing to force… only to allow…",
      text: null,
    },
    {
      id: 'limitless',
      name: 'LIMITLESS',
      startTime: 105000,
      duration: 15000, // 1:45-2:00
      description: 'Ocean / sky / open landscape',
      voice: "This is not the future…",
      text: null,
    },
    {
      id: 'installation',
      name: 'INSTALLATION',
      startTime: 120000,
      duration: 10000, // 2:00-2:10
      description: 'Soft fade / light wash',
      voice: "This is your reality now.",
      text: null,
      isFinal: true,
    },
  ],

  // Subliminal flashes at specific timestamps
  subliminals: [
    { time: 40000, text: 'RECEIVED' },   // 0:40
    { time: 65000, text: 'APPROVED' },   // 1:05
    { time: 85000, text: '£2,000,000' }, // 1:25
    { time: 110000, text: 'YOURS' },     // 1:50
  ],

  // Your Pexels images - served from backend static
  images: [
    '/api/static/bathe/wealth/scene1_arrival.jpg',     // Sunrise/horizon
    '/api/static/bathe/wealth/scene2_opening.jpg',     // Ocean/skyline
    '/api/static/bathe/wealth/scene3_state_entry.jpg', // Interior light
    '/api/static/bathe/wealth/scene4_entering_life.jpg', // Home
    '/api/static/bathe/wealth/scene5_normalised.jpg',  // Lifestyle
    '/api/static/bathe/wealth/scene6_expansion.jpg',   // Travel/motion
    '/api/static/bathe/wealth/scene7_limitless.jpg',   // Ocean/sky
    '/api/static/bathe/wealth/scene8_installation.jpg', // Soft fade
  ],
};

// Other categories (will be detailed later)
const BATHE_CONFIGS = {
  wealth: WEALTH_BATHE,
  love: {
    title: 'Love',
    color: '#E8A4A4',
    totalDuration: 130000,
    scenes: WEALTH_BATHE.scenes.map(s => ({ ...s })), // Placeholder - will customize
    subliminals: [
      { time: 40000, text: 'LOVED' },
      { time: 65000, text: 'CHOSEN' },
      { time: 85000, text: 'SAFE' },
      { time: 110000, text: 'CHERISHED' },
    ],
    images: WEALTH_BATHE.images, // Share wealth images for now
  },
  body: {
    title: 'Body',
    color: '#7BA378',
    totalDuration: 130000,
    scenes: WEALTH_BATHE.scenes.map(s => ({ ...s })),
    subliminals: [
      { time: 40000, text: 'VITAL' },
      { time: 65000, text: 'STRONG' },
      { time: 85000, text: 'HEALTHY' },
      { time: 110000, text: 'ALIVE' },
    ],
    images: WEALTH_BATHE.images,
  },
  performance: {
    title: 'Performance',
    color: '#F59E0B',
    totalDuration: 130000,
    scenes: WEALTH_BATHE.scenes.map(s => ({ ...s })),
    subliminals: [
      { time: 40000, text: 'FOCUS' },
      { time: 65000, text: 'EXECUTE' },
      { time: 85000, text: 'WIN' },
      { time: 110000, text: 'READY' },
    ],
    images: WEALTH_BATHE.images,
  },
  home: {
    title: 'Home',
    color: '#A89080',
    totalDuration: 130000,
    scenes: WEALTH_BATHE.scenes.map(s => ({ ...s })),
    subliminals: [
      { time: 40000, text: 'SAFE' },
      { time: 65000, text: 'BEAUTIFUL' },
      { time: 85000, text: 'BELONGING' },
      { time: 110000, text: 'HOME' },
    ],
    images: WEALTH_BATHE.images,
  },
  freedom: {
    title: 'Freedom',
    color: '#7BA3B8',
    totalDuration: 130000,
    scenes: WEALTH_BATHE.scenes.map(s => ({ ...s })),
    subliminals: [
      { time: 40000, text: 'FREE' },
      { time: 65000, text: 'LIMITLESS' },
      { time: 85000, text: 'EXPANSIVE' },
      { time: 110000, text: 'INFINITE' },
    ],
    images: WEALTH_BATHE.images,
  },
};

// =============================================================================
// SUBLIMINAL FLASH COMPONENT
// 1-2 frame flash, randomized slightly, positioned over video
// =============================================================================

const SubliminalFlash = ({ text, color, isVisible }) => {
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.05 }} // ~1-2 frames
          className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
        >
          <span 
            className="text-7xl font-bold tracking-[0.2em]"
            style={{ 
              color,
              textShadow: `0 0 60px ${color}80`
            }}
          >
            {text}
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// =============================================================================
// TEXT OVERLAY COMPONENT
// Appears with the voice, fades gently
// =============================================================================

const TextOverlay = ({ text, isVisible }) => {
  return (
    <AnimatePresence>
      {isVisible && text && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
          className="absolute bottom-1/4 left-0 right-0 text-center px-8 z-20"
        >
          <p 
            className="font-['Playfair_Display'] text-2xl text-white/90 italic"
            style={{ textShadow: '0 2px 30px rgba(0,0,0,0.8)' }}
          >
            "{text}"
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// =============================================================================
// VOICE TEXT COMPONENT
// Main voice line display - with silence/spacing between lines
// =============================================================================

const VoiceText = ({ text, isVisible }) => {
  return (
    <AnimatePresence mode="wait">
      {isVisible && text && (
        <motion.div
          key={text}
          initial={{ opacity: 0, filter: 'blur(10px)' }}
          animate={{ opacity: 1, filter: 'blur(0px)' }}
          exit={{ opacity: 0, filter: 'blur(10px)' }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
          className="absolute inset-0 flex items-center justify-center px-12 z-10"
        >
          <h2 
            className="font-['Playfair_Display'] text-3xl text-center leading-relaxed text-white"
            style={{ textShadow: '0 4px 40px rgba(0,0,0,0.7)' }}
          >
            {text}
          </h2>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// =============================================================================
// SCENE COMPONENT
// Image background with Ken Burns effect (subtle push-in zoom + slow pan)
// Slow crossfade transitions (1-1.5s)
// =============================================================================

const Scene = ({ scene, imageUrl, isActive, transitionDuration = 1500 }) => {
  const [showVoice, setShowVoice] = useState(false);
  const [showText, setShowText] = useState(false);

  useEffect(() => {
    if (isActive) {
      // Show voice after brief pause (audio spacing)
      const voiceTimer = setTimeout(() => setShowVoice(true), 800);
      
      // Show text overlay if scene has one (appears mid-scene)
      let textTimer;
      if (scene.text) {
        textTimer = setTimeout(() => setShowText(true), scene.duration * 0.4);
      }

      return () => {
        clearTimeout(voiceTimer);
        if (textTimer) clearTimeout(textTimer);
        setShowVoice(false);
        setShowText(false);
      };
    }
  }, [isActive, scene]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: isActive ? 1 : 0 }}
      transition={{ 
        duration: transitionDuration / 1000, // Slow crossfade 1-1.5s
        ease: 'easeInOut'
      }}
      className="absolute inset-0 overflow-hidden"
    >
      {/* Image Background with Ken Burns effect */}
      {imageUrl ? (
        <motion.div
          className="absolute inset-0"
          initial={{ scale: 1, x: 0, y: 0 }}
          animate={isActive ? { 
            scale: 1.15, // Subtle push-in zoom
            x: scene.id === 'limitless' ? -20 : 10, // Slight pan variation
            y: scene.id === 'expansion' ? -10 : 5,
          } : { scale: 1, x: 0, y: 0 }}
          transition={{ 
            duration: scene.duration / 1000,
            ease: 'linear'
          }}
        >
          <img
            src={imageUrl}
            alt={scene.name}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ 
              filter: 'brightness(0.55) saturate(1.15)',
            }}
          />
        </motion.div>
      ) : (
        // Soft fade / light wash for final scene (if no image)
        <motion.div
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: isActive ? 1 : 0 }}
          style={{
            background: 'radial-gradient(ellipse at center, rgba(203,161,83,0.15) 0%, rgba(0,0,0,0.95) 70%)'
          }}
        />
      )}

      {/* Dark gradient overlay */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.1) 40%, rgba(0,0,0,0.4) 100%)'
        }}
      />

      {/* Voice Text - Main affirmation */}
      <VoiceText text={scene.voice} isVisible={showVoice} />

      {/* Text Overlay - Secondary text */}
      <TextOverlay text={scene.text} isVisible={showText} />
    </motion.div>
  );
};

// =============================================================================
// PROGRESS BAR
// =============================================================================

const ProgressBar = ({ progress, color }) => {
  return (
    <div className="absolute bottom-8 left-8 right-8 z-30">
      <div className="h-[2px] bg-white/10 rounded-full overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ backgroundColor: color }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3, ease: 'linear' }}
        />
      </div>
    </div>
  );
};

// =============================================================================
// COMPLETION SCREEN
// =============================================================================

const CompletionScreen = ({ onClose }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 2 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1, duration: 1 }}
        className="text-center px-8"
      >
        <p className="text-[#CBA153] text-xs uppercase tracking-[0.3em] mb-6">
          Installation Complete
        </p>
        <h2 className="font-['Playfair_Display'] text-4xl text-white mb-12">
          This is your reality now.
        </h2>
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.5 }}
          onClick={onClose}
          className="text-white/50 text-sm hover:text-white transition-colors"
        >
          Return
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

// =============================================================================
// MAIN BATHE ENGINE COMPONENT
// =============================================================================

export const BatheEngine = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [showSubliminal, setShowSubliminal] = useState(false);
  const [currentSubliminal, setCurrentSubliminal] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  
  const startTimeRef = useRef(Date.now());
  const subliminalTriggeredRef = useRef(new Set());
  
  const config = BATHE_CONFIGS[category] || BATHE_CONFIGS.wealth;

  // ==========================================================================
  // MAIN TIMER - Progress and scene transitions
  // ==========================================================================
  
  useEffect(() => {
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      setElapsedTime(elapsed);
      
      // Update progress
      const newProgress = Math.min((elapsed / config.totalDuration) * 100, 100);
      setProgress(newProgress);

      // Check for scene transitions
      const currentScene = config.scenes.findIndex((scene, index) => {
        const nextScene = config.scenes[index + 1];
        if (!nextScene) return elapsed >= scene.startTime;
        return elapsed >= scene.startTime && elapsed < nextScene.startTime;
      });
      
      if (currentScene !== -1 && currentScene !== currentSceneIndex) {
        setCurrentSceneIndex(currentScene);
      }

      // Check completion
      if (elapsed >= config.totalDuration && !isComplete) {
        setIsComplete(true);
        clearInterval(interval);
      }
    }, 50); // High frequency for smooth progress

    return () => clearInterval(interval);
  }, [config, currentSceneIndex, isComplete]);

  // ==========================================================================
  // SUBLIMINAL SYSTEM - Triggered at specific timestamps
  // ==========================================================================
  
  useEffect(() => {
    config.subliminals.forEach((sub) => {
      // Check if we've passed this subliminal's time and haven't triggered it
      if (
        elapsedTime >= sub.time && 
        elapsedTime < sub.time + 500 && // 500ms window
        !subliminalTriggeredRef.current.has(sub.time)
      ) {
        subliminalTriggeredRef.current.add(sub.time);
        setCurrentSubliminal(sub.text);
        setShowSubliminal(true);

        // Hide after 1-2 frames (~50ms)
        setTimeout(() => {
          setShowSubliminal(false);
        }, 50);
      }
    });
  }, [elapsedTime, config.subliminals]);

  // ==========================================================================
  // HANDLERS
  // ==========================================================================

  const handleClose = useCallback(() => {
    navigate('/abundance');
  }, [navigate]);

  // ==========================================================================
  // RENDER
  // ==========================================================================

  return (
    <div className="fixed inset-0 bg-black overflow-hidden">
      {/* Subtle noise texture */}
      <div 
        className="fixed inset-0 pointer-events-none opacity-[0.025] z-40"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Scenes - with slow crossfade transitions */}
      {config.scenes.map((scene, index) => (
        <Scene
          key={scene.id}
          scene={scene}
          imageUrl={config.images?.[index]}
          isActive={currentSceneIndex === index}
          transitionDuration={1500} // 1.5 second crossfade
        />
      ))}

      {/* Subliminal Flash System */}
      <SubliminalFlash
        text={currentSubliminal}
        color={config.color}
        isVisible={showSubliminal}
      />

      {/* Header Controls */}
      <div className="absolute top-0 left-0 right-0 z-30 p-6 flex items-center justify-between">
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          whileHover={{ opacity: 1 }}
          onClick={handleClose}
          className="p-2 rounded-full hover:bg-white/10 transition-colors duration-300"
          data-testid="bathe-close"
        >
          <X size={24} className="text-white" />
        </motion.button>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          className="flex items-center gap-4"
        >
          <span 
            className="text-[10px] uppercase tracking-[0.25em] font-light"
            style={{ color: config.color }}
          >
            {config.title}
          </span>
          
          <button
            onClick={() => setIsMuted(!isMuted)}
            className="p-2 rounded-full hover:bg-white/10 transition-colors duration-300"
            data-testid="bathe-mute"
          >
            {isMuted ? (
              <VolumeX size={18} className="text-white/50" />
            ) : (
              <Volume2 size={18} className="text-white/50" />
            )}
          </button>
        </motion.div>
      </div>

      {/* Progress Bar */}
      <ProgressBar progress={progress} color={config.color} />

      {/* Completion Screen */}
      <AnimatePresence>
        {isComplete && (
          <CompletionScreen onClose={handleClose} />
        )}
      </AnimatePresence>
    </div>
  );
};

export default BatheEngine;
